import React, { useContext, useEffect, useMemo } from 'react';
import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { ConfluenceExperimentsContext } from '@post-office/growth-common/confluence-experiments-context';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import VideoIcon from '@atlaskit/icon/core/video';
import { Inline } from '@atlaskit/primitives/compiled';

import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
	UFOExperience,
} from '@atlassian/ufo/ufo-experimental';

import type {
	FlagDescriptor,
	FlagsStateContainer,
} from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { PartyIcon } from '../assets/PartyIcon.tsx';

const TEMPLATE_XFLOW_EXPERIMENT_KEY = 'twcg-2112-twc-xflow-conf-pages-templates';
const TEMPLATE_XFLOW_NATIVE_FLAG_RENDER_EXPERIENCE =
	'twcg-2112-confluence-template-xflow-native-flag-render';
export const TEMPLATE_XFLOW_NATIVE_FLAG_RENDER_TIMEOUT_MS = 5_000;

type TemplateXflowFlagIconProps = {
	onMount: () => void;
};

const TemplateXflowFlagIcon = ({ onMount }: TemplateXflowFlagIconProps) => {
	useEffect(() => {
		onMount();
	}, [onMount]);

	return <PartyIcon />;
};

export type TemplateXflowSuccessFlagSource = 'publishSuccessFlag' | 'shareSuccessFlag';

export type TemplateXflowSuccessFlag = {
	blueprintModuleCompleteKey: string;
	onDismiss: () => void;
	onTryItFree: () => void;
};

type UseTemplateXflowSuccessFlagProps = {
	cloudId: string | null | undefined;
	source: TemplateXflowSuccessFlagSource;
};

const i18n = defineMessages({
	templateXflowSuccessDescription: {
		id: 'share-and-restrict-dialog.template-xflow.flags.description',
		defaultMessage:
			'Skip the manual notes next time. Record your meeting and AI will write one for you.',
		description: 'Description of a share success flag promoting AI meeting notes',
	},
	templateXflowTryItFree: {
		id: 'share-and-restrict-dialog.template-xflow.flags.try-it-free',
		defaultMessage: 'Try it free',
		description: 'Action to try AI meeting notes from the share success flag',
	},
	templateXflowRemindMeLater: {
		id: 'share-and-restrict-dialog.template-xflow.flags.remind-me-later.ai-non-final',
		defaultMessage: 'Remind me later',
		description:
			'Action on the share success flag that dismisses the AI meeting notes promotion for now',
	},
});

export const useTemplateXflowSuccessFlag = ({
	cloudId,
}: UseTemplateXflowSuccessFlagProps): TemplateXflowSuccessFlag | undefined => {
	const { templateXflow } = useContext(ConfluenceExperimentsContext);
	const blueprintModuleCompleteKey =
		templateXflow?.currentPageTemplateData.blueprintModuleCompleteKey;

	return useMemo(() => {
		if (
			templateXflow?.currentPageTemplateData.isEligible !== true ||
			templateXflow.reminderState !== 'remind' ||
			templateXflow.isTerminallyDismissed ||
			!blueprintModuleCompleteKey ||
			!cloudId ||
			!templateXflow.openTwcXflowModal
		) {
			return undefined;
		}

		const dismissForSession = () => {
			templateXflow.dismissForSession();
		};

		return {
			blueprintModuleCompleteKey,
			onDismiss: dismissForSession,
			onTryItFree: () => {
				dismissForSession();
				templateXflow.openTwcXflowModal?.('confluence-xflow-flag');
			},
		};
	}, [blueprintModuleCompleteKey, cloudId, templateXflow]);
};

type ShowTemplateXflowSuccessFlagProps = {
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	extraFlagProps?: Pick<FlagDescriptor, 'close' | 'messageId' | 'messageType'>;
	flags: FlagsStateContainer;
	intl: IntlShape;
	source: TemplateXflowSuccessFlagSource;
	templateXflowFlag: TemplateXflowSuccessFlag;
	timeoutMs?: number;
	title: string;
};

export const showTemplateXflowSuccessFlag = async ({
	createAnalyticsEvent,
	extraFlagProps,
	flags,
	intl,
	source,
	templateXflowFlag,
	timeoutMs,
	title,
}: ShowTemplateXflowSuccessFlagProps): Promise<void> => {
	let flagId: string | number | undefined;
	let hasInteraction = false;
	let hasFinishedRenderExperience = false;
	const actionSubject =
		source === 'publishSuccessFlag'
			? 'templateXflowPublishSuccessFlag'
			: 'templateXflowShareSuccessFlag';
	const fireTemplateXflowEvent = (
		type: 'sendUIEvent' | 'sendOperationalEvent',
		action: 'clicked' | 'dismissed' | 'timedOut' | 'viewed',
		actionSubjectId: string,
	) =>
		createAnalyticsEvent?.({
			type,
			data: {
				action,
				actionSubject,
				actionSubjectId,
				source,
				attributes: {
					blueprintModuleCompleteKey: templateXflowFlag.blueprintModuleCompleteKey,
					experimentKey: TEMPLATE_XFLOW_EXPERIMENT_KEY,
					promptSurface: source,
					reminderState: 'remind',
				},
			},
		}).fire();
	const fireTemplateXflowReminderChanged = (sourceAction: 'tryItFree' | 'explicitDismiss') =>
		createAnalyticsEvent?.({
			type: 'sendTrackEvent',
			data: {
				action: 'changed',
				actionSubject: 'twcTemplateXflowReminder',
				source,
				attributes: {
					blueprintModuleCompleteKey: templateXflowFlag.blueprintModuleCompleteKey,
					experimentKey: TEMPLATE_XFLOW_EXPERIMENT_KEY,
					reminderState: 'dismissed',
					source,
					sourceAction,
				},
			},
		}).fire();
	const renderMetadata = {
		blueprintModuleCompleteKey: templateXflowFlag.blueprintModuleCompleteKey,
		experimentKey: TEMPLATE_XFLOW_EXPERIMENT_KEY,
		owner: 'growth-starburst',
		source,
	};
	const renderExperience = new UFOExperience(TEMPLATE_XFLOW_NATIVE_FLAG_RENDER_EXPERIENCE, {
		performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
		type: ExperienceTypes.Load,
		platform: { component: 'confluence' },
	});
	void renderExperience.start();
	const renderTimeoutId = setTimeout(() => {
		if (hasFinishedRenderExperience) {
			return;
		}

		hasFinishedRenderExperience = true;
		void renderExperience.failure({
			metadata: { ...renderMetadata, reason: 'mount-timeout' },
		});
	}, TEMPLATE_XFLOW_NATIVE_FLAG_RENDER_TIMEOUT_MS);
	const completeRenderExperience = () => {
		if (hasFinishedRenderExperience) {
			return;
		}

		hasFinishedRenderExperience = true;
		clearTimeout(renderTimeoutId);
		void renderExperience.success({ metadata: renderMetadata });
		fireTemplateXflowEvent('sendUIEvent', 'viewed', 'flag');
	};
	const failRenderExperience = (reason: string) => {
		if (hasFinishedRenderExperience) {
			return;
		}

		hasFinishedRenderExperience = true;
		clearTimeout(renderTimeoutId);
		void renderExperience.failure({ metadata: { ...renderMetadata, reason } });
	};

	const showFlagPromise = flags.showFlag({
		...extraFlagProps,
		type: 'custom',
		customIcon: <TemplateXflowFlagIcon onMount={completeRenderExperience} />,
		title,
		description: intl.formatMessage(i18n.templateXflowSuccessDescription),
		actions: [
			{
				content: (
					<Inline alignBlock="center" space="space.050">
						<VideoIcon label="" />
						{intl.formatMessage(i18n.templateXflowTryItFree)}
					</Inline>
				),
				onClick: () => {
					if (hasInteraction) {
						return;
					}
					hasInteraction = true;
					fireTemplateXflowEvent('sendUIEvent', 'clicked', 'tryItFree');
					fireTemplateXflowReminderChanged('tryItFree');
					templateXflowFlag.onTryItFree();
				},
			},
			{
				content: intl.formatMessage(i18n.templateXflowRemindMeLater),
				onClick: () => {
					if (hasInteraction) {
						return;
					}
					hasInteraction = true;
					fireTemplateXflowEvent('sendUIEvent', 'clicked', 'remindMeLater');
					void flags.hideFlag(flagId);
				},
			},
		],
		onClose: () => {
			if (hasInteraction) {
				return;
			}
			hasInteraction = true;
			fireTemplateXflowEvent('sendUIEvent', 'dismissed', 'explicitClose');
			fireTemplateXflowReminderChanged('explicitDismiss');
			templateXflowFlag.onDismiss();
		},
	});
	const flag = await showFlagPromise.catch((error: unknown) => {
		failRenderExperience('show-flag-rejected');
		throw error;
	});

	flagId = flag.id;
	if (timeoutMs !== undefined) {
		setTimeout(() => {
			if (!hasInteraction) {
				fireTemplateXflowEvent('sendOperationalEvent', 'timedOut', 'autoDismiss');
				hasInteraction = true;
			}
			void flags.hideFlag(flag.id);
		}, timeoutMs);
	}
};
